import openCompose, { openComposeWithViewState, prepareComposeViewState } from './openCompose';
import trySaveAndCloseCompose from './trySaveAndCloseCompose';
import upgradeCompose from '../actions/upgradeCompose';
import datapoints from '../datapoints';
import composeStore from '../store/composeStore';
import ComposeTarget from '../store/schema/ComposeTarget';
import { ComposeOperation } from '../store/schema/ComposeViewState';
import buildReplyForwardMessageBody from '../utils/buildReplyForwardMessageBody';
import { ComposeViewStateInitProps } from '../utils/createComposeViewState';
import createItemIdForRequest from '../utils/createItemIdForRequest';
import getBodyTypeForResponse from '../utils/getBodyTypeForResponse';
import getDocumentId from '../utils/getDocumentId';
import getQuotedBodyForSmimeItemReply from '../utils/getQuotedBodyForSmimeItemReply';
import getRespondSubject from '../utils/getRespondSubject';
import tryGetSmimeViewStateForResponse from '../utils/tryGetSmimeViewStateForResponse';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import getExtendedPropertyValue from '../utils/getExtendedPropertyValue';
import { ClientItemId, MailboxType } from 'owa-client-ids';
import isEncryptionEnabled from 'owa-encryption-common/lib/utils/isEncryptionEnabled';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ARCHIVE_FOLDERS_TREE_TYPE, SHARED_FOLDERS_TREE_TYPE } from 'owa-folders';
import { userMailInteractionAction } from 'owa-mail-actions';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { ClientItem, InstrumentationContext } from 'owa-mail-store';
import createItem from 'owa-mail-store/lib/services/createItem';
import createResponseFromModernGroupItem from 'owa-mail-store/lib/services/createResponseFromModernGroupItem';
import store from 'owa-mail-store/lib/store/Store';
import isMeetingRequest from 'owa-meeting-message/lib/utils/isMeetingRequest';
import { isDeepLink } from 'owa-popout';
import BodyType from 'owa-service/lib/contract/BodyType';
import ForwardItem from 'owa-service/lib/contract/ForwardItem';
import Item from 'owa-service/lib/contract/Item';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import Message from 'owa-service/lib/contract/Message';
import forwardItem from 'owa-service/lib/factory/forwardItem';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import getAttachmentsForSmimeResponse from 'owa-smime/lib/utils/getAttachmentsForSmimeResponse';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { isSxSDisplayed } from 'owa-sxs-store';
import isTabViewEnabled from 'owa-tab-store/lib/utils/isTabViewEnabled';
import { select } from 'satcheljs/lib/legacy';
import { isSmimeAttachment } from 'owa-attachment-model-store';

export interface ForwardMessageState {
    referenceItem: Item;
    primaryComposeId: string;
}

const LABEL_PROPERTY_NAME = 'msip_labels';

export default createLegacyActionWithDatapoint('forwardMessage', datapoints.MailComposeForward)(
    select<ForwardMessageState>({
        referenceItem: (referenceClientItemId: ClientItemId) =>
            store.items.get(referenceClientItemId.Id),
        primaryComposeId: () => composeStore.primaryComposeId,
    })(async function forwardMessage(
        referenceClientItemId: ClientItemId,
        actionSource: ActionSource,
        instrumentationContexts: InstrumentationContext[],
        suppressServerMarkReadOnReplyOrForward: boolean,
        state?: ForwardMessageState
    ) {
        const openInSxS = isSxSDisplayed();
        const openInTab = !isDeepLink() && isTabViewEnabled();
        if (!openInSxS && !openInTab) {
            await trySaveAndCloseCompose(null /*viewState*/);

            // If there is still default compose viewstate, it means it is failed to save and close previous compose
            // So no-op here
            if (state.primaryComposeId) {
                return Promise.resolve();
            }
        }

        const referenceItem = state.referenceItem as Message;
        const bodyType = getBodyTypeForResponse(referenceItem);
        const composeOperation = ComposeOperation.Forward;
        const documentId = getDocumentId(referenceItem);
        const referenceItemId = createItemIdForRequest(referenceItem.ItemId);

        const item = forwardItem({
            ReferenceItemId: referenceItemId,
            ReferenceItemDocumentId: documentId,
            Subject: getRespondSubject(referenceItem, composeOperation),
        });

        const mailboxInfo = referenceClientItemId.mailboxInfo;
        const groupId =
            mailboxInfo && mailboxInfo.type == MailboxType.GroupMailbox
                ? mailboxInfo.mailboxId
                : null;
        const openTarget = openInSxS
            ? ComposeTarget.SxS
            : openInTab
            ? ComposeTarget.SecondaryTab
            : ComposeTarget.PrimaryReadingPane;

        const smimeViewState = await tryGetSmimeViewStateForResponse(
            referenceItem as ClientItem,
            false /* isReplyOrReplyAll */
        );
        /**
         * Below check decides whether the forward mail will be an S/MIME mail
         * or not. This condition has to be first one in the below if..else block
         * because if S/MIME admin settings are enabled then by default every forward mail will
         * be an S/MIME mail. Also, if the message is an attachment of an S/MIME mail, we need to
         * ensure that we treat it like a S/MIME mail and not make the server call since server has no info about this mail.
         */
        if (
            !groupId &&
            isFeatureEnabled('cmp-smime') &&
            (isSmimeAttachment(referenceItem.ItemId.Id) ||
                isSmimeEnabledInViewState(smimeViewState))
        ) {
            const quotedBody = await getQuotedBodyForSmimeItemReply(
                referenceItem,
                composeOperation
            );
            const composeInitProps: ComposeViewStateInitProps = {
                operation: composeOperation,
                bodyType: bodyType,
                subject: item.Subject,
                referenceItemId: referenceItem.ItemId,
                referenceItemDocumentId: documentId,
                preferAsyncSend: false,
                useSmartResponse: false,
                quotedBody,
                attachments: getAttachmentsForSmimeResponse(
                    referenceItem.Attachments,
                    true /* isForward */
                ),
                smimeViewState,
                labelStringInReferenceItem: getExtendedPropertyValue(
                    referenceItem,
                    null,
                    LABEL_PROPERTY_NAME
                ),
            };

            await openCompose(composeInitProps, openTarget, null /* groupId */);
        } else {
            const composeInitProps: ComposeViewStateInitProps = {
                operation: composeOperation,
                bodyType: bodyType,
                subject: item.Subject,
                referenceItemId: referenceItem.ItemId,
                referenceItemDocumentId: documentId,
                preferAsyncSend: true,
                IRMData: isEncryptionEnabled() ? referenceItem.RightsManagementLicenseData : null,
                useSmartResponse: true, // Set useSmartResponse to true in case we can't get quoted body
                labelStringInReferenceItem: getExtendedPropertyValue(
                    referenceItem,
                    null,
                    LABEL_PROPERTY_NAME
                ),
            };

            if (isFeatureEnabled('cmp-upgradeCompose')) {
                const viewState = await prepareComposeViewState(composeInitProps, groupId);
                await openComposeWithViewState(viewState, openTarget);

                upgradeCompose(
                    viewState,
                    createForward(groupId, item, bodyType, suppressServerMarkReadOnReplyOrForward)
                );
            } else {
                const message: Message = await createForward(
                    groupId,
                    item,
                    bodyType,
                    suppressServerMarkReadOnReplyOrForward
                );
                if (message) {
                    composeInitProps.itemId = message.ItemId;
                    composeInitProps.quotedBody = buildReplyForwardMessageBody(
                        message.Body.Value,
                        bodyType
                    );
                    composeInitProps.attachments = message.Attachments;
                    composeInitProps.useSmartResponse = false; // Quoted body is loaded, no need to use smart response now

                    if (message.ItemClass) {
                        composeInitProps.meetingRequestItem =
                            isMeetingRequest(message.ItemClass) != null ? message : null;
                    }

                    if (message.From) {
                        composeInitProps.from = message.From.Mailbox;
                    }
                }

                await openCompose(composeInitProps, openTarget, groupId);
            }
        }

        userMailInteractionAction('Forward', instrumentationContexts);
    })
);

async function createForward(
    groupId: string,
    item: ForwardItem,
    bodyType: BodyType,
    suppressServerMarkReadOnReplyOrForward: boolean
): Promise<Message> {
    const timeFormat = getUserConfiguration().UserOptions.TimeFormat;
    const selectedNode = getSelectedNode();
    // To fix the XRF access issue, we set RemoteExecute for archive and shared folder scenarios.
    // For CreateItem, we do not set X-AnchorMailbox header, the request will always hit primary mailbox user's mailbox server.
    // On server side code, we have code fix to check the flag when reply/replyall/forward emails.
    const remoteExecute = !!(
        selectedNode &&
        (selectedNode.treeType === SHARED_FOLDERS_TREE_TYPE ||
            selectedNode.treeType === ARCHIVE_FOLDERS_TREE_TYPE)
    );
    try {
        const createForwardResponse = groupId
            ? await createResponseFromModernGroupItem(
                  groupId,
                  item,
                  bodyType,
                  false /*isSend*/,
                  timeFormat
              )
            : await createItem(
                  item,
                  bodyType,
                  false /*isSend*/,
                  timeFormat,
                  false /* isInlineCompose */,
                  null /* IRMData */,
                  null /*folderId*/,
                  remoteExecute,
                  suppressServerMarkReadOnReplyOrForward
              );

        const responseMessage = createForwardResponse.ResponseMessages
            .Items[0] as ItemInfoResponseMessage;
        return responseMessage.Items[0] as Message;
    } catch (error) {
        return null;
    }
}
